import React from 'react';
import { ReservationUtils, TranslationUtils } from '../../utils';
import { Button } from '../Button/Button';
import { IconCheckSquare } from '../../assets/icons';
import { FormattedDate } from 'react-intl';
import { RequiredActions, Reservation } from 'guest-data-types-ts-lib';
import { StoreState } from '../../types/store.type';
import { Loading } from '../Loading/Loading';
import { BackButton } from '../BackButton/BackButton';
import horizontalLogo from '../../assets/images/vacasa-logo-horizontal-light.svg';
import { useSelector } from 'react-redux';

interface LegacyRentalAgreementProps {
    isSubmitDisabled: boolean;
    isSubmitLoading: boolean;
    isCheckPoliciesDisabled: boolean;
    checkPolicies: boolean;
    handleSubmit: () => Promise<void>;
    handleChangeAgreement: (e: any) => void;
    handleOnPrintClick: () => void;
}

export const LegacyRentalAgreement: React.FC<LegacyRentalAgreementProps> = (props) => {
    const trip = useSelector((store: StoreState) => store.trip);
    const { unit, stay, guests } = trip;

    let requiredActions: RequiredActions | undefined;
    let reservation: Reservation | undefined;

    const {
        isSubmitDisabled,
        isSubmitLoading,
        isCheckPoliciesDisabled,
        checkPolicies,
        handleSubmit,
        handleChangeAgreement,
        handleOnPrintClick,
    } = props;

    if (trip.isLoading) {
        return <Loading />;
    }

    requiredActions = trip.required_actions;
    reservation = trip.reservation;

    if (!requiredActions || !unit || !stay || !guests || !reservation) {
        return null;
    }

    const { contract_agreement: contractAgreement } = requiredActions;

    const formatCompletedDate = (completedDate: string) => {
        return <FormattedDate value={new Date(completedDate)} day="numeric" month="short" year="numeric" />;
    };

    const listLength = (contractAgreement?.terms.summary_text.match(/<li/g) || []).length;
    const bookedBy = `${guests.primary.first_name} ${guests.primary.last_name}`;
    const bookedOnDate = formatCompletedDate(reservation.creation_date!);
    const reservationCodeFormated = ReservationUtils.formatConfirmationCode(
        reservation.confirmation_code ? reservation.confirmation_code : ''
    );
    return (
        <div>
            <BackButton path="/reservation" />
            <div className="text-center printable">
                <p>{`${TranslationUtils.get('confirmation')} ${reservationCodeFormated} | Vacasa`}</p>
                <img className="horizontal-logo-rental" src={horizontalLogo} alt="Vacasa" />
            </div>
            <h1 tabIndex={0} className="page-title no-print">
                {TranslationUtils.get('rental_agreement')}
            </h1>
            {contractAgreement?.requires_wet_signature && !contractAgreement.completed && (
                <div tabIndex={0} className="alert alert-secondary no-print print-bg" role="alert">
                    {TranslationUtils.get('print_header_text')}
                    <div tabIndex={0} className="row mt-3">
                        <div className="col-1 col-md-1 font-bold number-style">1</div>
                        <div className="col-11 col-md-5 font-bold">{TranslationUtils.get('print_step_one')}</div>
                        <div className="col-1 col-md-1 font-bold number-style">2</div>
                        <div className="col-11 col-md-5 font-bold">{TranslationUtils.get('print_step_two')}</div>
                    </div>
                    <div tabIndex={0} className="row mt-3">
                        <div className="offset-md-1 col-md-5">
                            <Button label={TranslationUtils.get('print_button')} variant="default" onClick={handleOnPrintClick} />
                        </div>

                        <div className="offset-md-1 col-md-5">
                            <p className="mt-0 mb-1 font-bold">
                                {`${TranslationUtils.get('print_email_label')}: `}
                                <a href={`mailto:${TranslationUtils.get('print_email_value')}`}>
                                    {TranslationUtils.get('print_email_value')}
                                </a>
                            </p>
                            <p className="mt-0 mb-1 font-bold">
                                {`${TranslationUtils.get('print_fax_label')}: ${TranslationUtils.get('print_fax_value')}`}
                            </p>
                        </div>
                    </div>
                </div>
            )}
            {contractAgreement?.completed && contractAgreement.completed_date ? (
                <div tabIndex={0} className="message-agreement">
                    <div className="check-icon">
                        <IconCheckSquare />
                    </div>

                    <span tabIndex={0}>
                        {TranslationUtils.get('agreed_on', { agreedOn: formatCompletedDate(contractAgreement.completed_date) })}
                    </span>
                </div>
            ) : (
                <React.Fragment />
            )}
            <h5 tabIndex={0} id="print-title">
                {contractAgreement?.terms.summary_title}
            </h5>
            <div tabIndex={0} id="print-header" className="hidden d-print-block">
                <br />
                <hr />
                <div className="row">
                    <div className="col-md-6">
                        <p>{`${TranslationUtils.get('confirmation')} ${reservationCodeFormated}`}</p>
                        <p>
                            {`${TranslationUtils.get('print_booked_by')} ${bookedBy} ${TranslationUtils.get('print_on')} `}
                            {bookedOnDate}
                        </p>
                        <p>Check-in {formatCompletedDate(stay.check_in_date_time)}</p>
                        <p>Check-out {formatCompletedDate(stay.check_out_date_time)}</p>
                    </div>
                    <div className="col-md-6">
                        <p>{unit.name}</p>
                        <p>{unit.address_1}</p>
                        <p>{`${unit.city}, ${unit.state} ${unit.zip}`}</p>
                    </div>
                    <br />
                </div>
                <hr />
            </div>
            {contractAgreement?.wet_signature_details && (
                <div tabIndex={0} className="printable">
                    <br />
                    <div dangerouslySetInnerHTML={{ __html: contractAgreement.wet_signature_details.header_one }} />
                    <br />
                    <div dangerouslySetInnerHTML={{ __html: contractAgreement.wet_signature_details.header_two }} />
                    <br />
                </div>
            )}

            <div
                tabIndex={0}
                id="responsible-parties"
                dangerouslySetInnerHTML={{ __html: contractAgreement?.terms.responsible_parties || '' }}
            />

            <div tabIndex={0} dangerouslySetInnerHTML={{ __html: contractAgreement?.terms.summary_text || '' }} />
            {contractAgreement?.terms.rental_text && (
                <ol tabIndex={0} start={listLength + 1} type="A">
                    <li>
                        <div className="terms-title" dangerouslySetInnerHTML={{ __html: contractAgreement.terms.rental_title }} />
                        <div className="textarea-agreement">
                            <div dangerouslySetInnerHTML={{ __html: contractAgreement.terms.rental_text }} />
                        </div>
                    </li>
                </ol>
            )}

            {contractAgreement?.terms.unit_text && (
                <ol tabIndex={0} start={contractAgreement.terms.rental_text ? listLength + 2 : listLength + 1} type="A">
                    <li>
                        <div className="terms-title" dangerouslySetInnerHTML={{ __html: contractAgreement.terms.unit_title }} />
                        <div className="textarea-agreement">
                            <div dangerouslySetInnerHTML={{ __html: contractAgreement.terms.unit_text }} />
                        </div>
                    </li>
                </ol>
            )}

            <div tabIndex={0} className="terms-addendums">
                <div className="" dangerouslySetInnerHTML={{ __html: contractAgreement?.terms.addendum_one || '' }} />
                <div className="" dangerouslySetInnerHTML={{ __html: contractAgreement?.terms.addendum_two || '' }} />
            </div>

            {!contractAgreement?.requires_wet_signature && !contractAgreement?.completed && (
                <div tabIndex={0} className="no-print">
                    <div className="rental-form">
                        <div>
                            <input
                                type="checkbox"
                                checked={checkPolicies}
                                onChange={handleChangeAgreement}
                                disabled={isCheckPoliciesDisabled}
                            />
                        </div>
                        <span className="form-check-text">{TranslationUtils.get('i_agree_terms')}</span>
                    </div>

                    <div className="rental-buttons">
                        <Button
                            label="Submit"
                            variant="primary"
                            className="rental-submit-button"
                            onClick={handleSubmit}
                            disabled={isSubmitDisabled}
                            isLoading={isSubmitLoading}
                        />
                    </div>
                </div>
            )}
            {contractAgreement?.wet_signature_details && (
                <div id="print-footer" className="printable">
                    <br></br>
                    <div dangerouslySetInnerHTML={{ __html: contractAgreement.wet_signature_details.footer }} />
                </div>
            )}
        </div>
    );
};
